/**
 * Who may open the prospects pages, read off GET /me.
 *
 * The service decides and the gateway carries its answer: an admin here is an
 * owner there, anybody else needs a seat (Settings > Prospecting > People).
 * GET /me answers 200 for both, with the role, and refuses a person with no
 * seat. This turns the query's state into the one thing a page or the
 * sidebar needs to know, with nothing of React in it so it can be covered.
 */
import { isProspectingError } from './api'
import type { Me } from './types'

export type ProspectingAccess =
  | { state: 'loading' }
  | { state: 'allowed'; role: Me['role']; me: Me }
  /** Signed in, and this account has no seat. */
  | { state: 'no-access' }
  /** This app's session is gone. The sign-in screen is already on its way. */
  | { state: 'signed-out' }
  /** This server has no prospects service, or it did not answer. */
  | { state: 'unavailable'; message: string }
  | { state: 'failed'; message: string }

/** The sentence for a server with no prospects service behind it. */
export const NOT_SET_UP_MESSAGE = 'Prospecting is not set up on this server.'

export function accessFrom(query: { isPending: boolean; data?: Me | undefined; error?: unknown }): ProspectingAccess {
  if (query.data !== undefined) return { state: 'allowed', role: query.data.role === 'owner' ? 'owner' : 'member', me: query.data }
  if (query.isPending) return { state: 'loading' }
  const error = query.error
  if (isProspectingError(error)) {
    if (error.kind === 'no-access' || error.kind === 'forbidden') return { state: 'no-access' }
    if (error.kind === 'signed-out') return { state: 'signed-out' }
    // The gateway is not mounted on a server with no service, so the route itself is not there.
    if (error.kind === 'not-found') return { state: 'unavailable', message: NOT_SET_UP_MESSAGE }
    if (error.kind === 'unreachable') return { state: 'unavailable', message: error.message }
    return { state: 'failed', message: error.message }
  }
  return { state: 'failed', message: error instanceof Error ? error.message : 'Prospecting could not be opened.' }
}

/**
 * Whether the sidebar draws the Prospecting rows: for an admin always (an
 * owner there, whatever the service says right now), and for anybody else
 * once GET /me says they have a seat.
 */
export function showsProspecting(isAdmin: boolean, access: ProspectingAccess): boolean {
  return isAdmin || access.state === 'allowed'
}
